import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Typography, Space, Result } from 'antd';
import { CrownOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  isLoggedIn,
  isSubscriptionExpired,
  getSubscriptionExpiryDate,
  getUserRole,
  isSubscriptionGatingEnabled,
  isSubscriptionNearingExpiry,
  getUser,
  setHasActiveSubscription,
} from '../../utils/auth';
import { shouldShowProfileCompletionAlert } from '../../utils/studentProfileLoginFlow';
import { ROUTES } from '../../config/routes';
import SubscriptionService from '../../services/subscription/SubscriptionService';

const { Text, Title, Paragraph } = Typography;

const SUBSCRIPTION_GUARD_DISMISS_KEY = 'subscriptionGuardDismissedAt';
const DISMISS_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
const POST_LOGIN_PROMPTS_KEY = 'postLoginPromptsShown';

/** Roles without a dedicated subscription-management page fall back to plan selection. */
const getPlansRoute = (role: string | null): string => {
  if (role === 'ORGANIZATION') return ROUTES.ORGANIZATION.SUBSCRIPTION;
  return ROUTES.PUBLIC.CHOOSE_PLAN;
};

type GuardState =
  | { kind: 'none' }
  | { kind: 'expired'; reason: 'expired' | 'missing' }
  | { kind: 'nearing' }
  | { kind: 'upsell'; profileIncomplete: boolean };

const SubscriptionGuard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<GuardState>({ kind: 'none' });

  const role = getUserRole();

  const isExcludedRoute = useCallback((): boolean => {
    const excluded = [
      ROUTES.ORGANIZATION.SUBSCRIPTION,
      ROUTES.PUBLIC.LOGIN,
      ROUTES.PUBLIC.REGISTER,
      ROUTES.PUBLIC.CHOOSE_PLAN,
      ROUTES.PUBLIC.PAYMENT_RETURN,
    ];
    return excluded.includes(location.pathname as any);
  }, [location.pathname]);

  const wasRecentlyDismissed = useCallback((): boolean => {
    const dismissedAt = Number(sessionStorage.getItem(SUBSCRIPTION_GUARD_DISMISS_KEY) || 0);
    if (!dismissedAt) return false;
    return Date.now() - dismissedAt < DISMISS_COOLDOWN_MS;
  }, []);

  const evaluate = useCallback(() => {
    if (!isLoggedIn() || !isSubscriptionGatingEnabled()) {
      setState({ kind: 'none' });
      return;
    }

    // Hard block takes priority everywhere except the plan/renewal pages themselves —
    // this restricts the rest of the app to "go choose/renew a plan" only, per spec.
    const expired = isSubscriptionExpired();
    if (expired) {
      if (isExcludedRoute()) {
        setState({ kind: 'none' });
        return;
      }
      const sub = localStorage.getItem('subscription');
      setState({ kind: 'expired', reason: sub ? 'expired' : 'missing' });
      return;
    }

    if (isExcludedRoute()) {
      setState({ kind: 'none' });
      return;
    }

    if (isSubscriptionNearingExpiry() && !wasRecentlyDismissed()) {
      setState({ kind: 'nearing' });
      return;
    }

    // Once per browser session: upsell other plans + nudge incomplete profile.
    if (!sessionStorage.getItem(POST_LOGIN_PROMPTS_KEY)) {
      const profileIncomplete = shouldShowProfileCompletionAlert(getUser());
      setState({ kind: 'upsell', profileIncomplete });
      return;
    }

    setState({ kind: 'none' });
  }, [isExcludedRoute, wasRecentlyDismissed]);

  useEffect(() => {
    evaluate();
    // Re-check periodically so an expiry that happens mid-session (no navigation)
    // still locks the app down without requiring a logout/login cycle.
    const interval = setInterval(evaluate, 60 * 1000);
    return () => clearInterval(interval);
  }, [evaluate]);

  useEffect(() => {
    if (!isLoggedIn() || !isSubscriptionGatingEnabled()) return;
    // Self-heal stale/incorrect localStorage state — e.g. sessions established before the
    // hasActiveSubscription flag existed, or a subscription changed via the admin backend —
    // by confirming the real status with the server once per mount, then re-evaluating.
    SubscriptionService.getCurrentSubscription()
      .then((res) => {
        if (res.status === 'success') {
          const sub = res.data;
          const active = !!sub && sub.statusCode === 'ACTIVE';
          setHasActiveSubscription(active);
          if (active && sub?.endDate) {
            localStorage.setItem('subscriptionExpiryDate', sub.endDate);
          } else if (!active) {
            localStorage.removeItem('subscriptionExpiryDate');
          }
        }
      })
      .catch(() => {})
      .finally(() => evaluate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismissNearing = () => {
    sessionStorage.setItem(SUBSCRIPTION_GUARD_DISMISS_KEY, String(Date.now()));
    setState({ kind: 'none' });
  };

  const handleDismissUpsell = () => {
    sessionStorage.setItem(POST_LOGIN_PROMPTS_KEY, '1');
    setState({ kind: 'none' });
  };

  const goToPlans = () => {
    sessionStorage.setItem(POST_LOGIN_PROMPTS_KEY, '1');
    navigate(getPlansRoute(role));
  };

  const expiryDate = getSubscriptionExpiryDate();

  // --- Hard block: full-screen takeover, no dismiss — matches "restrict all pages" spec.
  if (state.kind === 'expired') {
    return (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(255,255,255,0.98)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}
      >
        <Result
          icon={<CrownOutlined style={{ color: '#faad14' }} />}
          title={state.reason === 'expired' ? 'Your Subscription Has Expired' : 'No Active Subscription'}
          subTitle={
            state.reason === 'expired'
              ? `Your subscription${expiryDate ? ` expired on ${new Date(expiryDate).toLocaleDateString()}` : ''}. Renew now to keep using enpropeL.`
              : "You don't have an active subscription yet. Choose a plan to unlock all features."
          }
          extra={
            <Button type="primary" size="large" onClick={goToPlans}>
              {state.reason === 'expired' ? 'Renew Now' : 'Choose a Plan'}
            </Button>
          }
        />
      </div>
    );
  }

  // --- Soft reminder: dismissible, cooldown-based.
  if (state.kind === 'nearing') {
    return (
      <Modal
        open
        closable={false}
        maskClosable={false}
        footer={
          <Space>
            <Button onClick={handleDismissNearing}>Later</Button>
            <Button type="primary" icon={<ClockCircleOutlined />} onClick={goToPlans}>
              Renew Now
            </Button>
          </Space>
        }
        width={460}
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Title level={4} style={{ marginBottom: 8 }}>Your Subscription Is Ending Soon</Title>
          <Text>
            {expiryDate
              ? `Your plan expires on ${new Date(expiryDate).toLocaleDateString()}.`
              : 'Your plan is about to expire.'} Renew now to avoid any interruption.
          </Text>
        </div>
      </Modal>
    );
  }

  // --- Upsell + profile-completion nudge: shown once per session.
  if (state.kind === 'upsell') {
    return (
      <Modal
        open
        onCancel={handleDismissUpsell}
        footer={
          <Space>
            <Button onClick={handleDismissUpsell}>Maybe Later</Button>
            <Button type="primary" icon={<CrownOutlined />} onClick={goToPlans}>
              View Plans
            </Button>
          </Space>
        }
        width={460}
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Title level={4} style={{ marginBottom: 8 }}>
            <CrownOutlined style={{ color: '#667eea' }} /> Explore Other Plans
          </Title>
          <Paragraph>
            Check out other subscription plans — you might find one with better features for your needs.
          </Paragraph>
          {state.profileIncomplete && (
            <Paragraph style={{ marginTop: 16 }}>
              <UserOutlined style={{ color: '#faad14', marginRight: 6 }} />
              Your profile isn't complete yet — finish it to get better matches and recommendations.
            </Paragraph>
          )}
        </div>
      </Modal>
    );
  }

  return null;
};

export default SubscriptionGuard;
